import React, { useState, useEffect, useCallback } from 'react';import { useNavigate, useParams, Link } from 'react-router-dom';
import axios, { API } from '../services/api';
import { warmSWRCache, peekSWRCache } from '../services/useSWRCache';
import { useHealth } from '../context/HealthContext';
import { toast } from 'sonner';
import { motion, AnimatePresence } from 'framer-motion';
import {
  X, Check, CaretRight, Spinner, ArrowLeft, FilePdf, Image,
  Trash, Download, Tag, CheckCircle, XCircle, Warning,
  Pill, Heart, Lightning, Calendar, Brain, Flask, Eye,
  EnvelopeSimple
} from '@phosphor-icons/react';
import SendEmailModal from '../components/shared/SendEmailModal';
// Stage 20B+ Wave F — first-upload feedback pulse.
import PulseFeedback from '../components/feedback/PulseFeedback';
// Wave 4 Phase 1B — compact "What matters most" hero + Read-deeper disclosure
import { WhatMattersMost } from '../components/documents/WhatMattersMost';
import { ReadDeeper } from '../components/documents/ReadDeeper';

const FACT_TYPE_CONFIG = {
  condition: { icon: Heart, color: 'text-rose-600', bg: 'bg-rose-50', border: 'border-rose-200', label: 'Condition' },
  medication: { icon: Pill, color: 'text-blue-600', bg: 'bg-blue-50', border: 'border-blue-200', label: 'Medication' },
  symptom: { icon: Lightning, color: 'text-amber-600', bg: 'bg-amber-50', border: 'border-amber-200', label: 'Symptom' },
  event: { icon: Calendar, color: 'text-indigo-600', bg: 'bg-indigo-50', border: 'border-indigo-200', label: 'Event' },
  appointment: { icon: Calendar, color: 'text-teal-600', bg: 'bg-teal-50', border: 'border-teal-200', label: 'Appointment' },
  abnormal_finding: { icon: Flask, color: 'text-red-600', bg: 'bg-red-50', border: 'border-red-200', label: 'Abnormal Finding' },
  memory_fact: { icon: Brain, color: 'text-slate-600', bg: 'bg-slate-50', border: 'border-slate-200', label: 'Note' },
};

const FactCard = React.memo(({ fact, onConfirm, onDismiss, confirming }) => {
  const config = FACT_TYPE_CONFIG[fact.type] || FACT_TYPE_CONFIG.memory_fact;
  const Icon = config.icon;

  return (
    <motion.div
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -8, height: 0 }}
      className={`border ${config.border} ${config.bg} rounded-xl p-4`}
      data-testid={`fact-card-${fact.id}`}
    >
      <div className="flex items-start gap-3">
        <div className={`mt-0.5 ${config.color}`}>
          <Icon size={20} weight="fill" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-1">
            <span className={`text-xs font-medium px-2 py-0.5 rounded-full ${config.bg} ${config.color} border ${config.border}`}>
              {config.label}
            </span>
            {fact.data?.confidence && (
              <span className={`text-xs ${fact.data.confidence === 'high' ? 'text-green-600' : fact.data.confidence === 'medium' ? 'text-amber-600' : 'text-slate-400'}`}>
                {fact.data.confidence} confidence
              </span>
            )}
          </div>
          <p className="text-sm font-medium text-slate-900">{fact.label}</p>
          <p className="text-xs text-slate-500 mt-1">{fact.what_changed}</p>
          {fact.data?.context && (
            <p className="text-xs text-slate-400 mt-1 italic">"{fact.data.context}"</p>
          )}
        </div>
      </div>
      <div className="flex gap-2 mt-3 ml-8">
        <button
          data-testid={`confirm-fact-${fact.id}`}
          onClick={() => onConfirm(fact.id)}
          disabled={confirming}
          className="flex-1 flex items-center justify-center gap-1.5 px-3 py-2 bg-[#235347] text-white rounded-lg text-sm font-medium hover:bg-[#1A3F36] transition-colors disabled:opacity-50"
        >
          {confirming ? <Spinner size={14} className="animate-spin" /> : <Check size={14} />}
          Save
        </button>
        <button
          data-testid={`dismiss-fact-${fact.id}`}
          onClick={() => onDismiss(fact.id)}
          disabled={confirming}
          className="flex-1 flex items-center justify-center gap-1.5 px-3 py-2 bg-white border border-slate-200 text-slate-600 rounded-lg text-sm font-medium hover:bg-slate-50 transition-colors disabled:opacity-50"
        >
          <X size={14} />
          Dismiss
        </button>
      </div>
    </motion.div>
  );
});

const AutoSavedBadge = ({ item }) => {
  const config = FACT_TYPE_CONFIG[item.type] || FACT_TYPE_CONFIG.memory_fact;
  const Icon = config.icon;
  return (
    <div className="flex items-center gap-2 px-3 py-2 bg-green-50 border border-green-200 rounded-lg">
      <Icon size={16} className="text-green-600" />
      <span className="text-sm text-green-700">{item.label}</span>
      <CheckCircle size={14} className="text-green-500 ml-auto" />
    </div>
  );
};

// ─── Stage 20B — "How this fits your profile" ──────────────────────────
// Self-fetches the user's conditions + medications so the card renders
// immediately without prop-plumbing through the parent. SAFE BY DESIGN:
// we never invent clinical connections — we only display the user's
// existing context alongside the report's extracted concepts, and offer
// a chat prompt CTA so the LLM can do the actual interpretation under
// its safety prompt. No diagnostic claims live in this card.
const ProfileFitCard = ({ documentId, extraction }) => {
  const [conditions, setConditions] = useState([]);
  const [medications, setMedications] = useState([]);
  const [loaded, setLoaded] = useState(false);
  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const [c, m] = await Promise.all([
          axios.get(`${API}/conditions`).then(r => r.data || []).catch(() => []),
          axios.get(`${API}/medications`).then(r => r.data || []).catch(() => []),
        ]);
        if (!alive) return;
        setConditions(Array.isArray(c) ? c.filter(x => (x.status || 'active') === 'active') : []);
        setMedications(Array.isArray(m) ? m.filter(x => (x.status || 'active') === 'active') : []);
        setLoaded(true);
      } catch { /* ignore */ }
    })();
    return () => { alive = false; };
  }, [documentId]);

  // Empty-state guard — only render once we've loaded the profile.
  if (!loaded) return null;
  // If the user has zero profile data, render a calm prompt to add some.
  const hasProfile = conditions.length > 0 || medications.length > 0;
  // Pull the report's headline concepts.
  const reportConditions = (extraction?.candidate_facts?.conditions || [])
    .map(f => f.name || f.fact_value || '').filter(Boolean).slice(0, 3);
  // Stage 20B — corrected to use candidate_facts.abnormal_findings (the actual
  // backend shape). Was reading non-existent `key_findings` → field never had data.
  const reportFindings = (extraction?.candidate_facts?.abnormal_findings || [])
    .map(f => `${f.finding || ''}: ${f.value || ''}`.trim().replace(/^:\s*/, '')).filter(Boolean).slice(0, 3);

  // The chat prompt that opens when the user taps "Ask Kindra how these connect".
  // Pre-fills a non-leading question so the LLM does the interpretation safely.
  const askText = hasProfile && (reportConditions.length || reportFindings.length)
    ? `Looking at this report, can you talk me through how it might connect to what's already in my profile (${conditions.slice(0,2).map(c => c.name).join(', ') || 'my conditions'}${medications.length ? ' and ' + medications.slice(0,1).map(m => m.name).join(', ') : ''})?`
    : 'Can you talk me through how this report fits my health profile?';

  return (
    <div className="card-base p-5" data-testid="document-profile-fit">
      <div className="flex items-center justify-between mb-2">
        <h3 className="font-medium text-slate-900">How this fits your profile</h3>
        <span className="text-[10px] uppercase tracking-[0.14em] text-slate-400">Context</span>
      </div>
      <p className="text-xs text-slate-400 mb-4">
        Kindra holds your record alongside this report so the conversation isn't starting from scratch.
      </p>

      {hasProfile ? (
        <div className="space-y-3.5">
          {conditions.length > 0 && (
            <div>
              <p className="text-[10.5px] font-semibold uppercase tracking-[0.14em] text-slate-500 mb-1.5">Conditions Kindra knows</p>
              <div className="flex flex-wrap gap-1.5">
                {conditions.slice(0, 6).map((c, i) => (
                  <span key={i} className="text-[12px] px-2.5 py-1 rounded-full bg-[#235347]/8 text-[#1c4439] border border-[#235347]/15">
                    {c.name}
                  </span>
                ))}
              </div>
            </div>
          )}
          {medications.length > 0 && (
            <div>
              <p className="text-[10.5px] font-semibold uppercase tracking-[0.14em] text-slate-500 mb-1.5">Current medications</p>
              <div className="flex flex-wrap gap-1.5">
                {medications.slice(0, 6).map((m, i) => (
                  <span key={i} className="text-[12px] px-2.5 py-1 rounded-full bg-slate-50 text-slate-700 border border-slate-200">
                    {m.name}{m.frequency ? ` · ${m.frequency}` : ''}
                  </span>
                ))}
              </div>
            </div>
          )}
          {(reportConditions.length > 0 || reportFindings.length > 0) && (
            <div>
              <p className="text-[10.5px] font-semibold uppercase tracking-[0.14em] text-slate-500 mb-1.5">This report adds</p>
              <div className="flex flex-wrap gap-1.5">
                {reportConditions.map((c, i) => (
                  <span key={`rc-${i}`} className="text-[12px] px-2.5 py-1 rounded-full bg-amber-50 text-amber-800 border border-amber-200/70">
                    {c}
                  </span>
                ))}
                {reportFindings.map((f, i) => (
                  <span key={`rf-${i}`} className="text-[12px] px-2.5 py-1 rounded-full bg-amber-50/60 text-amber-800 border border-amber-200/50">
                    {f}
                  </span>
                ))}
              </div>
            </div>
          )}
          <Link
            to={`/chat?prompt=${encodeURIComponent(askText)}`}
            className="inline-flex items-center gap-1.5 text-[12.5px] font-medium text-[#235347] hover:text-[#1c4439] mt-1 group"
            data-testid="profile-fit-ask-kindra"
          >
            Ask Kindra how these connect
            <CaretRight size={12} className="transition-transform group-hover:translate-x-0.5" />
          </Link>
          <p className="mt-2 text-[10.5px] text-slate-400 italic leading-snug">
            Kindra explains and prepares — it doesn't diagnose or prescribe. Real interpretation happens with your clinician.
          </p>
        </div>
      ) : (
        <div>
          <p className="text-sm text-slate-600 mb-3">
            Add a condition or medication to your profile and Kindra will use that context every time it answers about this report.
          </p>
          <Link
            to="/health"
            className="inline-flex items-center gap-1.5 text-[12.5px] font-medium text-[#235347] hover:text-[#1c4439] group"
            data-testid="profile-fit-go-profile"
          >
            Update health profile
            <CaretRight size={12} className="transition-transform group-hover:translate-x-0.5" />
          </Link>
        </div>
      )}
    </div>
  );
};

// ─── Stage 20B — "Things to track over time" ────────────────────────────
// Surfaces abnormal markers from this report so the user knows what's
// worth re-testing or watching, without inventing retesting intervals
// (only the clinician can set those). Tap → asks Kindra "when should I
// re-test" so the LLM provides general guidance under safety constraints.
// Backend ships these under `candidate_facts.abnormal_findings` with the
// shape { finding, value, reference_range, severity, confidence, page }.
const ThingsToTrackCard = ({ extraction }) => {
  const flagged = ((extraction?.candidate_facts || {}).abnormal_findings || []).slice(0, 6);
  if (flagged.length === 0) return null;

  const trackText = `Looking at this report, which of these results would be worth tracking over time, and roughly how often might my doctor want to recheck them?`;
  return (
    <div className="card-base p-5" data-testid="document-things-to-track">
      <div className="flex items-center justify-between mb-2">
        <h3 className="font-medium text-slate-900">Things to track over time</h3>
        <span className="text-[10px] uppercase tracking-[0.14em] text-slate-400">For your record</span>
      </div>
      <p className="text-xs text-slate-400 mb-4">
        Kindra will compare these markers automatically when you upload future reports.
      </p>
      <ul className="space-y-2.5 mb-4">
        {flagged.map((f, i) => (
          <li key={i} className="flex items-start gap-2.5 text-[13.5px] text-slate-700">
            <span className="inline-block w-1.5 h-1.5 rounded-full bg-amber-500 mt-[7px] flex-shrink-0" />
            <div className="flex-1 min-w-0">
              <span className="font-medium text-[#0E1C2E]">{f.finding}</span>
              {f.value && <span className="text-slate-500"> · {f.value}</span>}
              {f.reference_range && <span className="text-slate-400 text-[11.5px]"> (range {f.reference_range})</span>}
              {f.severity && <span className="text-amber-700 text-[11px] font-medium ml-1.5 uppercase tracking-[0.08em]">{f.severity}</span>}
            </div>
          </li>
        ))}
      </ul>
      <Link
        to={`/chat?prompt=${encodeURIComponent(trackText)}`}
        className="inline-flex items-center gap-1.5 text-[12.5px] font-medium text-[#235347] hover:text-[#1c4439] group"
        data-testid="things-to-track-ask"
      >
        Ask Kindra when to re-test
        <CaretRight size={12} className="transition-transform group-hover:translate-x-0.5" />
      </Link>
    </div>
  );
};

const DocumentDetailPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { refreshHealthDebounced } = useHealth();
  // P0 Stage-14b — cache-first hydration.
  // If we already prefetched this doc (e.g. immediately after upload analysis
  // completed, or because it appeared in the /documents list cache), render
  // it instantly with NO loading skeleton. The fetchDocument below still runs
  // in the background to revalidate.
  const cachePeek = peekSWRCache(`/documents/${id}`);
  const [document, setDocument] = useState(cachePeek);
  const [extraction, setExtraction] = useState(null);
  const [loading, setLoading] = useState(!cachePeek);
  const [confirmingId, setConfirmingId] = useState(null);
  const [confirmingAll, setConfirmingAll] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [formData, setFormData] = useState(cachePeek || {});
  const [showEmailModal, setShowEmailModal] = useState(false);

  const fetchDocument = useCallback(async () => {
    try {
      // P0 perf — race the two GETs in parallel. Was serial: doc → then extraction
      // (2× round-trip = 100-400ms). Now: Promise.all = 1× round-trip.
      // Extraction is requested speculatively; if status is 'analyzing'/'failed'
      // we discard the result.
      const [docRes, extRes] = await Promise.all([
        axios.get(`${API}/documents/${id}`),
        axios.get(`${API}/documents/${id}/extraction`).catch(() => null),
      ]);
      const data = docRes.data;
      setDocument(data);
      setFormData(data);
      // Warm the cache so back-nav from this page to /documents instantly
      // shows this doc with its latest state (and so a subsequent visit to
      // this same detail page is instant).
      warmSWRCache(`/documents/${id}`, data);

      // NOTE: 'complete' is legacy seed/migration value; treat identically to 'completed'
      if (extRes && ['completed', 'complete', 'needs_review'].includes(data.extraction_status)) {
        setExtraction(extRes.data);
      }
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    fetchDocument();
  }, [fetchDocument]);

  // Poll while analyzing
  useEffect(() => {
    if (document?.extraction_status !== 'analyzing') return;
    const interval = setInterval(async () => {
      try {
        const { data } = await axios.get(`${API}/documents/${id}`);
        setDocument(data);
        if (['completed', 'complete', 'needs_review', 'failed'].includes(data.extraction_status)) {
          clearInterval(interval);
          if (['completed', 'complete', 'needs_review'].includes(data.extraction_status)) {
            const extRes = await axios.get(`${API}/documents/${id}/extraction`);
            setExtraction(extRes.data);
          }
        }
      } catch { /* ignore */ }
    }, 3000);
    return () => clearInterval(interval);
  }, [document?.extraction_status, id]);

  const confirmFact = async (factId) => {
    // Phase 2A perf — optimistic UI: update state INSTANTLY before the await,
    // roll back on error. Button no longer freezes waiting for backend.
    const target = extraction?.needs_confirmation?.find(f => f.id === factId);
    if (!target) return;
    const prevExtraction = extraction;
    setExtraction(prev => prev && ({
      ...prev,
      needs_confirmation: (prev.needs_confirmation || []).filter(f => f.id !== factId),
      confirmed_facts: [...(prev.confirmed_facts || []), { ...target, status: 'confirmed' }],
    }));
    toast.success(`Saved: ${target.label || 'Finding'}`);
    try {
      await axios.post(`${API}/documents/${id}/facts/${factId}/confirm`);
      refreshHealthDebounced();
    } catch (err) {
      console.error(err);
      setExtraction(prevExtraction);  // rollback
      toast.error('Failed to save finding. Please try again.');
    }
  };

  const dismissFact = async (factId) => {
    // Phase 2A perf — optimistic UI (same pattern as confirmFact).
    const target = extraction?.needs_confirmation?.find(f => f.id === factId);
    if (!target) return;
    const prevExtraction = extraction;
    setExtraction(prev => prev && ({
      ...prev,
      needs_confirmation: (prev.needs_confirmation || []).filter(f => f.id !== factId),
      dismissed_facts: [...(prev.dismissed_facts || []), { ...target, status: 'dismissed' }],
    }));
    toast('Dismissed', { description: target.label || 'Finding removed' });
    try {
      await axios.post(`${API}/documents/${id}/facts/${factId}/dismiss`);
    } catch (err) {
      console.error(err);
      setExtraction(prevExtraction);  // rollback
      toast.error('Failed to dismiss finding. Please try again.');
    }
  };

  const confirmAllFacts = async () => {
    setConfirmingAll(true);
    try {
      const res = await axios.post(`${API}/documents/${id}/facts/confirm-all`);
      await fetchDocument();
      refreshHealthDebounced();
      toast.success(`All ${res.data?.confirmed_count || ''} findings saved to your health record`);
    } catch (err) {
      console.error(err);
      toast.error('Failed to save all findings. Please try again.');
    } finally {
      setConfirmingAll(false);
    }
  };

  const retryAnalysis = async () => {
    try {
      setDocument(prev => ({ ...prev, extraction_status: 'analyzing' }));
      setExtraction(null);
      await axios.post(`${API}/documents/${id}/analyze`);
    } catch (err) {
      console.error(err);
    }
  };

  const updateDocument = async () => {
    try {
      await axios.put(`${API}/documents/${id}`, formData);
      await fetchDocument();
      setShowEditModal(false);
    } catch (err) {
      console.error(err);
    }
  };

  const deleteDocument = async () => {
    if (window.confirm('Delete this document?')) {
      try {
        await axios.delete(`${API}/documents/${id}`);
        navigate('/documents');
      } catch (err) {
        console.error(err);
      }
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#FAFAFA] flex items-center justify-center" data-testid="document-loading">
        <Spinner size={32} className="animate-spin text-[#235347]" />
      </div>
    );
  }

  if (!document) {
    return (
      <div className="min-h-screen bg-[#FAFAFA] flex flex-col items-center justify-center">
        <p className="text-slate-500">Document not found</p>
        <button onClick={() => navigate('/documents')} className="mt-4 text-[#235347]">Go back</button>
      </div>
    );
  }

  const pendingFacts = extraction?.needs_confirmation || [];
  const autoSaved = extraction?.auto_saved?.filter(f => f.status === 'saved') || [];
  const confirmedFacts = extraction?.confirmed_facts || [];

  return (
    <div className="min-h-screen bg-[#FAFAFA] flex flex-col pb-8" data-testid="document-detail">
      <header className="glass-header sticky top-0 z-10 px-6 py-4 flex items-center justify-between">
        <div className="flex items-center gap-3 min-w-0 flex-1">
          <button
            onClick={() => navigate('/documents')}
            className="text-slate-600 hover:text-slate-900 min-w-[44px] min-h-[44px] flex items-center justify-center -ml-2 rounded-lg hover:bg-slate-100"
            data-testid="back-to-documents"
            aria-label="Back to documents"
          >
            <ArrowLeft size={22} />
          </button>
          <h1 className="font-medium text-slate-900 truncate">{document.filename}</h1>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={() => setShowEditModal(true)} className="p-2 hover:bg-slate-100 rounded-lg" data-testid="edit-document-btn">
            <Tag size={20} className="text-slate-600" />
          </button>
          {extraction?.summary && (
            <button onClick={() => setShowEmailModal(true)} className="p-2 hover:bg-slate-100 rounded-lg" data-testid="email-document-summary-btn" title="Send summary by email">
              <EnvelopeSimple size={20} className="text-[#235347]" />
            </button>
          )}
          <a
            href={`${API}/documents/${id}/file`}
            target="_blank"
            rel="noopener noreferrer"
            className="p-2 hover:bg-slate-100 rounded-lg"
            data-testid="download-document-btn"
          >
            <Download size={20} className="text-slate-600" />
          </a>
          <button onClick={deleteDocument} className="p-2 hover:bg-red-50 rounded-lg" data-testid="delete-document-btn">
            <Trash size={20} className="text-red-500" />
          </button>
        </div>
      </header>

      <div className="flex-1 px-6 py-4 space-y-8">
        {/* File Info */}
        <div className="card-base p-5" data-testid="document-info-card">
          <div className="flex items-center gap-4 mb-4">
            <div className="w-14 h-14 bg-slate-100 rounded-xl flex items-center justify-center">
              {document.file_type.includes('pdf')
                ? <FilePdf size={28} className="text-red-500" />
                : <Image size={28} className="text-blue-500" />
              }
            </div>
            <div>
              <p className="font-medium text-slate-900">{document.filename}</p>
              <p className="text-sm text-slate-500">
                {(document.file_size / 1024).toFixed(1)} KB
                {document.document_type && document.document_type !== 'unknown' && ` \u00B7 ${document.document_type.replace(/_/g, ' ')}`}
                {' \u00B7 '}{document.category}
              </p>
            </div>
          </div>
          {document.description && <p className="text-slate-700 text-sm">{document.description}</p>}
          {document.document_date && (
            <p className="text-sm text-slate-500 mt-2">
              Document date: {new Date(document.document_date).toLocaleDateString()}
            </p>
          )}
        </div>

        {/* Image Preview */}
        {document.file_type.startsWith('image/') && (
          <div className="card-base p-4">
            {/*
              Wave 3 fix sprint (2026-02): cap image preview height so a tall
              portrait phone photo (e.g. 3000×4000 source) doesn't dominate
              the page. `object-fit: contain` preserves aspect ratio inside
              the cap. Lazy + async decode so the 5-10 MB original doesn't
              block the rest of the page from rendering. Wrapping anchor lets
              the user tap to open the full-size original in a new tab.
            */}
            <a
              href={`${API}/documents/${id}/file`}
              target="_blank"
              rel="noreferrer"
              className="block"
              data-testid="document-image-link"
            >
              <img
                src={`${API}/documents/${id}/file`}
                alt={document.filename}
                loading="lazy"
                decoding="async"
                className="w-full max-h-[60vh] rounded-lg object-contain bg-[#F4F1EB]"
                style={{ maxWidth: '100%', height: 'auto' }}
                data-testid="document-image-preview"
              />
            </a>
            <p className="mt-2 text-[11px] text-slate-400 text-center">Tap image to view full size</p>
          </div>
        )}

        {/* Analysis Status */}
        {document.extraction_status === 'analyzing' && (
          <div className="card-base p-5" data-testid="analyzing-indicator">
            <div className="flex items-center gap-3">
              <Spinner size={24} className="animate-spin text-[#235347]" />
              <div>
                <p className="font-medium text-slate-900">Reading your report…</p>
                <p className="text-sm text-slate-500">Pulling out what matters. This usually takes about a minute.</p>
              </div>
            </div>
          </div>
        )}

        {document.extraction_status === 'failed' && (
          <div className="card-base p-5 border-red-200" data-testid="analysis-failed">
            <div className="flex items-center gap-3">
              <Warning size={24} className="text-red-500" />
              <div className="flex-1">
                <p className="font-medium text-slate-900">Kindra couldn't clearly read this yet</p>
                <p className="text-sm text-slate-500 mt-1">
                  {document.extraction_error || "This upload doesn't contain readable clinical information yet. You can retry, or upload a clearer version."}
                </p>
              </div>
              <button
                onClick={retryAnalysis}
                className="px-4 py-2 bg-[#235347] text-white rounded-lg text-sm font-medium hover:bg-[#1A3F36] flex-shrink-0"
                data-testid="retry-analysis-btn"
              >
                Retry
              </button>
            </div>
          </div>
        )}

        {/* Wave 4 Phase 1B P1 — Compact "What matters most" hero.
            Sits ABOVE the verbose sections and answers the four questions
            from the founder's brief in one viewport: what changed · what
            Kindra remembered · what Kindra will watch · what to ask. */}
        {extraction && (
          <WhatMattersMost
            extraction={extraction}
            pendingCount={pendingFacts.length}
            autoSavedCount={autoSaved.length}
            confirmedCount={confirmedFacts.length}
          />
        )}

        {/* Document Summary — Wave 4 Phase 1B: detail prose moved into the
            "Read deeper" disclosure further down. The compact summary is now
            already in the WhatMattersMost hero above.
            (Original section kept commented-out for traceability:
              {extraction?.summary && ( ... )} ) */}

        {/* Pending Facts — needs confirmation
            Wave 4 Phase 1B/1C: header + copy reframed twice — first from
            "Review Findings" (admin) to "Awaiting your confirmation" (1B),
            and now to "Help Kindra confirm these details" (1C — companion
            collaborative voice). */}
        {pendingFacts.length > 0 && (
          <div className="card-base p-5" data-testid="pending-facts-section">
            <div className="flex items-center justify-between mb-4">
              <div>
                <h3 className="font-medium text-slate-900">Help Kindra confirm these details</h3>
                <p className="text-xs text-slate-500 mt-0.5">{pendingFacts.length} item{pendingFacts.length > 1 ? 's' : ''} below — tap to keep or skip</p>
              </div>
              {pendingFacts.length > 1 && (
                <button
                  onClick={confirmAllFacts}
                  disabled={confirmingAll}
                  className="flex items-center gap-1.5 px-3 py-1.5 bg-[#235347] text-white rounded-lg text-xs font-medium hover:bg-[#1A3F36] disabled:opacity-50"
                  data-testid="confirm-all-facts-btn"
                >
                  {confirmingAll ? <Spinner size={12} className="animate-spin" /> : <CheckCircle size={14} />}
                  Save All
                </button>
              )}
            </div>
            <AnimatePresence mode="popLayout">
              <div className="space-y-3">
                {pendingFacts.map(fact => (
                  <FactCard
                    key={fact.id}
                    fact={fact}
                    onConfirm={confirmFact}
                    onDismiss={dismissFact}
                    confirming={confirmingId === fact.id}
                  />
                ))}
              </div>
            </AnimatePresence>
          </div>
        )}

        {/* Auto-saved Facts — Wave 4 Phase 1C: companion-language reframe.
            "Now part of your health record" replaces "Added to your record
            from this report" — softer, less workflow-feeling. */}
        {autoSaved.length > 0 && (
          <div className="card-base p-5" data-testid="auto-saved-section">
            <h3 className="font-medium text-slate-900 mb-2">Now part of your health record</h3>
            <p className="text-xs text-slate-500 mb-3">Kindra will reach for these in future conversations.</p>
            <div className="space-y-2">
              {autoSaved.map(item => <AutoSavedBadge key={item.id} item={item} />)}
            </div>
          </div>
        )}

        {/* Confirmed Facts */}
        {confirmedFacts.length > 0 && (
          <div className="card-base p-5" data-testid="confirmed-facts-section">
            <h3 className="font-medium text-slate-900 mb-3">Saved to your record</h3>
            <div className="space-y-2">
              {confirmedFacts.map((item, i) => (
                <div key={i} className="flex items-center gap-2 px-3 py-2 bg-green-50 border border-green-200 rounded-lg">
                  <CheckCircle size={16} className="text-green-600" />
                  <span className="text-sm text-green-700">{item.label}</span>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Wave 4 Phase 1B P1 — Read deeper. Collapses the verbose educational
            content (full Summary essay, Plain language, Why this matters,
            Things to clarify, Profile fit, Things-to-track explanation,
            Suggested questions detail, Providers & Dates) under ONE toggle so
            the first viewport stays calm. */}
        <ReadDeeper label="Read deeper">
          {/* Full document summary essay */}
          {extraction?.summary && (
            <div className="card-base p-5" data-testid="document-summary">
              <h3 className="font-medium text-slate-900 mb-3">Document summary</h3>
              <div className="p-4 bg-[#235347]/5 rounded-xl">
                <p className="text-slate-700 text-sm leading-relaxed">{extraction.summary}</p>
              </div>
              {extraction.plain_language && extraction.plain_language !== extraction.summary && (
                <div className="mt-3">
                  <h4 className="text-xs font-medium text-slate-500 uppercase mb-1">What this means</h4>
                  <p className="text-sm text-slate-600 leading-relaxed">{extraction.plain_language}</p>
                </div>
              )}
              {extraction.significance && (
                <div className="mt-3">
                  <h4 className="text-xs font-medium text-slate-500 uppercase mb-1">Why this matters</h4>
                  <p className="text-sm text-slate-600 leading-relaxed">{extraction.significance}</p>
                </div>
              )}
            </div>
          )}

          {/* Things to Clarify */}
          {extraction?.uncertainties?.length > 0 && (
            <div className="card-base p-5" data-testid="document-uncertainties">
              <h3 className="font-medium text-slate-900 mb-3">Things to clarify</h3>
              <p className="text-xs text-slate-400 mb-3">These items may need follow-up or further explanation from your doctor.</p>
              <ul className="space-y-2">
                {extraction.uncertainties.map((u, i) => (
                  <li key={i} className="flex items-start gap-2.5 text-sm text-slate-600">
                    <Warning size={16} className="text-amber-500 mt-0.5 flex-shrink-0" />
                    {u}
                  </li>
                ))}
              </ul>
            </div>
          )}

          {/* Suggested Questions — full list (the WMM hero shows top 3) */}
          {extraction?.suggested_questions?.length > 3 && (
            <div className="card-base p-5" data-testid="document-suggested-questions">
              <h3 className="font-medium text-slate-900 mb-3">More questions worth raising</h3>
              <ul className="space-y-2">
                {extraction.suggested_questions.slice(3).map((q, i) => (
                  <li key={i} className="flex items-start gap-2.5 text-sm text-slate-600">
                    <CaretRight size={14} className="text-[#235347] mt-1 flex-shrink-0" />
                    {q}
                  </li>
                ))}
              </ul>
            </div>
          )}

          {/* Stage 20B — "How this fits your profile" + "Things to track over time" */}
          <ProfileFitCard documentId={id} extraction={extraction} />
          <ThingsToTrackCard extraction={extraction} />
        </ReadDeeper>

        {/* Providers & Dates */}
        {(extraction?.providers_mentioned?.length > 0 || extraction?.dates_found?.length > 0) && (
          <div className="card-base p-5" data-testid="providers-dates-section">
            {extraction.providers_mentioned?.length > 0 && (
              <div className="mb-4">
                <h4 className="text-xs font-medium text-slate-500 uppercase mb-2">Providers Mentioned</h4>
                <div className="flex flex-wrap gap-2">
                  {extraction.providers_mentioned.map((p, i) => (
                    <span key={i} className="px-3 py-1 bg-slate-100 rounded-full text-sm text-slate-700">{p}</span>
                  ))}
                </div>
              </div>
            )}
            {extraction.dates_found?.length > 0 && (
              <div>
                <h4 className="text-xs font-medium text-slate-500 uppercase mb-2">Dates Found</h4>
                <ul className="space-y-1">
                  {extraction.dates_found.map((d, i) => (
                    <li key={i} className="text-sm text-slate-600 flex items-start gap-2">
                      <CaretRight size={14} className="text-slate-400 mt-0.5 flex-shrink-0" />
                      {d}
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        )}

        {/* Linked Items */}
        {(document.linked_conditions?.length > 0 || document.linked_appointment) && (
          <div className="card-base p-5">
            <h3 className="font-medium text-slate-900 mb-3">Linked To</h3>
            {document.linked_conditions?.length > 0 && (
              <div className="flex flex-wrap gap-2 mb-2">
                {document.linked_conditions.map((c, i) => (
                  <span key={i} className="px-3 py-1 bg-slate-100 rounded-full text-sm text-slate-700">{c}</span>
                ))}
              </div>
            )}
            {document.linked_appointment && (
              <p className="text-sm text-slate-500">Linked to appointment</p>
            )}
          </div>
        )}

        {/* All done indicator */}
        {extraction && pendingFacts.length === 0 && document.extraction_status === 'completed' && (
          <div className="text-center py-4" data-testid="analysis-complete-indicator">
            <CheckCircle size={32} className="mx-auto text-green-500 mb-2" />
            <p className="text-sm font-medium text-green-700">Analysis complete</p>
            <p className="text-xs text-slate-500 mt-1">All findings have been reviewed</p>
          </div>
        )}

        {/* Stage 20B+ Wave F — first-upload pulse feedback. Renders ONCE
            globally (localStorage-gated by surface key); fires only when
            extraction is fully done so the user has had a chance to see
            the value before being asked. Self-dismisses on send/close. */}
        {extraction && document.extraction_status === 'completed' && (
          <PulseFeedback
            surface="first_upload"
            prompt="Was this useful?"
            placeholder="What stood out — or what didn't quite land?"
            context={{ document_id: document.id, document_title: document.title }}
          />
        )}
      </div>

      {/* Edit Modal */}
      <AnimatePresence>
        {showEditModal && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/50 z-50 flex items-end"
            onClick={() => setShowEditModal(false)}
          >
            <motion.div
              initial={{ y: '100%' }}
              animate={{ y: 0 }}
              exit={{ y: '100%' }}
              onClick={(e) => e.stopPropagation()}
              className="bg-white w-full rounded-t-3xl p-6 max-h-[80vh] overflow-auto"
            >
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-lg font-medium text-slate-900">Edit Document</h2>
                <button onClick={() => setShowEditModal(false)} className="text-slate-400">
                  <X size={24} />
                </button>
              </div>
              <div className="space-y-4">
                <input
                  placeholder="Filename"
                  value={formData.filename || ''}
                  onChange={(e) => setFormData(prev => ({ ...prev, filename: e.target.value }))}
                  className="w-full bg-slate-50 border border-slate-200 rounded-xl px-4 py-3 outline-none"
                  data-testid="edit-filename-input"
                />
                <select
                  value={formData.category || 'other'}
                  onChange={(e) => setFormData(prev => ({ ...prev, category: e.target.value }))}
                  className="w-full bg-slate-50 border border-slate-200 rounded-xl px-4 py-3 outline-none"
                  data-testid="edit-category-select"
                >
                  <option value="report">Report</option>
                  <option value="pathology">Pathology</option>
                  <option value="imaging">Imaging</option>
                  <option value="letter">Letter</option>
                  <option value="discharge">Discharge Summary</option>
                  <option value="prescription">Prescription</option>
                  <option value="other">Other</option>
                </select>
                <textarea
                  placeholder="Description"
                  value={formData.description || ''}
                  onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
                  className="w-full bg-slate-50 border border-slate-200 rounded-xl px-4 py-3 outline-none min-h-[80px] resize-none"
                  data-testid="edit-description-input"
                />
                <input
                  type="date"
                  placeholder="Document date"
                  value={formData.document_date || ''}
                  onChange={(e) => setFormData(prev => ({ ...prev, document_date: e.target.value }))}
                  className="w-full bg-slate-50 border border-slate-200 rounded-xl px-4 py-3 outline-none"
                  data-testid="edit-date-input"
                />
              </div>
              <button
                onClick={updateDocument}
                className="w-full mt-6 bg-[#235347] text-white rounded-full px-6 py-3 font-medium hover:bg-[#1A3F36]"
                data-testid="save-document-changes-btn"
              >
                Save Changes
              </button>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Email Modal */}
      <AnimatePresence>
        {showEmailModal && (
          <SendEmailModal
            reportType="document_summary"
            reportId={id}
            defaultSubject={`Document Summary — ${document?.filename || 'Document'}`}
            onClose={() => setShowEmailModal(false)}
          />
        )}
      </AnimatePresence>
    </div>
  );
};

export default DocumentDetailPage;
